import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FolderIcon, MagnifyingGlassIcon, PlusIcon, StarIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarSolid } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { projectService } from '../services/projectService';
import { formatDate } from '../lib/format';
import { Project, ProjectStatus } from '../types';
import CreateProjectModal from '../components/CreateProjectModal';
import { EmptyState, LoadingState, PageHeader, StatusBadge } from '../components/ui';

type StatusFilter = 'all' | ProjectStatus;

const Projects: React.FC = () => {
  const { t, language } = useLanguage();
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [ownProjects, setOwnProjects] = useState<Project[]>([]);
  const [sharedProjects, setSharedProjects] = useState<Project[]>([]);
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [favIds, own, shared] = await Promise.all([
        projectService.getUserFavoriteProjectIds(user.uid),
        isAdmin ? projectService.getAllProjects() : projectService.getUserProjects(user.uid),
        isAdmin ? Promise.resolve([] as Project[]) : projectService.getProjectsWhereUserIsMember(user.email || ''),
      ]);
      setFavoriteIds(favIds);
      setOwnProjects(own);
      // A project you own may also list you as a member; show it once.
      const ownIds = new Set(own.map((p) => p.id));
      setSharedProjects(shared.filter((p) => !ownIds.has(p.id)));
    } catch (error) {
      console.error('Error loading projects:', error);
      toast.error(t('projects.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [user, isAdmin, t]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleFavorite = async (e: React.MouseEvent, projectId: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) return;
    const isFav = favoriteIds.has(projectId);
    setFavoriteIds((prev) => {
      const next = new Set(prev);
      isFav ? next.delete(projectId) : next.add(projectId);
      return next;
    });
    try {
      if (isFav) await projectService.removeFavorite(projectId, user.uid);
      else await projectService.addFavorite(projectId, user.uid);
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error(t('common.genericError'));
      load();
    }
  };

  const matches = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (p: Project) =>
      (statusFilter === 'all' || (p.status || 'active') === statusFilter) &&
      (!favoritesOnly || favoriteIds.has(p.id)) &&
      (!needle || p.name.toLowerCase().includes(needle) || p.description.toLowerCase().includes(needle));
  }, [search, statusFilter, favoritesOnly, favoriteIds]);

  if (loading) return <LoadingState />;

  const visibleOwn = ownProjects.filter(matches);
  const visibleShared = sharedProjects.filter(matches);
  const nothingAtAll = ownProjects.length === 0 && sharedProjects.length === 0;
  const filters: { value: StatusFilter; label: string }[] = [
    { value: 'active', label: t('projects.status.active') },
    { value: 'archived', label: t('projects.status.archived') },
    { value: 'deleted', label: t('projects.status.deleted') },
    { value: 'all', label: t('projects.status.all') },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('projects.title')}
        description={isAdmin ? t('projects.adminSubtitle') : t('projects.subtitle')}
        actions={
          <button type="button" onClick={() => setShowCreate(true)} className="btn btn-primary">
            <PlusIcon className="h-4 w-4" />
            {t('projects.new')}
          </button>
        }
      />

      {!nothingAtAll && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1 sm:max-w-xs">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('projects.search')}
              aria-label={t('projects.search')}
              className="input pl-9"
            />
          </div>
          <div className="flex flex-wrap items-center gap-1" role="group" aria-label={t('projects.filterByStatus')}>
            {filters.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => setStatusFilter(f.value)}
                aria-pressed={statusFilter === f.value}
                className={`btn btn-sm ${statusFilter === f.value ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900' : 'btn-ghost'}`}
              >
                {f.label}
              </button>
            ))}
            <span className="mx-1 hidden h-5 w-px bg-gray-200 dark:bg-gray-800 sm:block" />
            <button
              type="button"
              onClick={() => setFavoritesOnly((v) => !v)}
              aria-pressed={favoritesOnly}
              className={`btn btn-sm ${favoritesOnly ? 'bg-amber-50 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300' : 'btn-ghost'}`}
            >
              {favoritesOnly ? <StarSolid className="h-4 w-4 text-amber-400" /> : <StarIcon className="h-4 w-4" />}
              {t('projects.favoritesOnly')}
            </button>
          </div>
        </div>
      )}

      {nothingAtAll ? (
        <div className="card">
          <EmptyState
            icon={FolderIcon}
            title={t('projects.empty')}
            text={t('projects.emptyText')}
            action={
              <button type="button" onClick={() => setShowCreate(true)} className="btn btn-primary">
                <PlusIcon className="h-4 w-4" />
                {t('projects.new')}
              </button>
            }
          />
        </div>
      ) : (
        <>
          <ProjectGrid
            title={isAdmin ? t('projects.allProjects') : t('projects.mine')}
            projects={visibleOwn}
            favoriteIds={favoriteIds}
            onToggleFavorite={toggleFavorite}
            language={language}
            emptyText={t('projects.noMatch')}
          />
          {sharedProjects.length > 0 && (
            <ProjectGrid
              title={t('projects.sharedWithMe')}
              projects={visibleShared}
              favoriteIds={favoriteIds}
              onToggleFavorite={toggleFavorite}
              language={language}
              emptyText={t('projects.noMatch')}
            />
          )}
        </>
      )}

      {showCreate && (
        <CreateProjectModal
          onClose={() => setShowCreate(false)}
          onSuccess={(id) => {
            setShowCreate(false);
            navigate(`/projects/${id}`);
          }}
        />
      )}
    </div>
  );
};

const ProjectGrid: React.FC<{
  title: string;
  projects: Project[];
  favoriteIds: Set<string>;
  onToggleFavorite: (e: React.MouseEvent, id: string) => void;
  language: 'en' | 'hu';
  emptyText: string;
}> = ({ title, projects, favoriteIds, onToggleFavorite, language, emptyText }) => {
  const { t } = useLanguage();
  return (
    <section>
      <h2 className="mb-3 text-sm font-semibold text-gray-500 dark:text-gray-400">
        {title} <span className="font-normal text-gray-400">({projects.length})</span>
      </h2>
      {projects.length === 0 ? (
        <p className="rounded-xl border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-500 dark:border-gray-800">
          {emptyText}
        </p>
      ) : (
        <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {projects.map((project) => {
            const isFav = favoriteIds.has(project.id);
            return (
              <li key={project.id}>
                <Link
                  to={`/projects/${project.id}`}
                  className="card group flex h-full flex-col p-4 transition-colors hover:border-primary-300 dark:hover:border-primary-700"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-2">
                      <h3 className="truncate font-semibold text-gray-900 group-hover:text-primary-700 dark:text-white dark:group-hover:text-primary-300">
                        {project.name}
                      </h3>
                      <StatusBadge status={project.status} />
                    </div>
                    <button
                      type="button"
                      onClick={(e) => onToggleFavorite(e, project.id)}
                      className="-m-1.5 shrink-0 rounded-md p-1.5 hover:bg-amber-50 dark:hover:bg-amber-950/40"
                      aria-label={isFav ? t('projects.unfavorite') : t('projects.favorite')}
                      aria-pressed={isFav}
                      title={isFav ? t('projects.unfavorite') : t('projects.favorite')}
                    >
                      {isFav ? (
                        <StarSolid className="h-5 w-5 text-amber-400" />
                      ) : (
                        <StarIcon className="h-5 w-5 text-gray-300 group-hover:text-gray-400 dark:text-gray-600" />
                      )}
                    </button>
                  </div>
                  <p className="mt-1.5 line-clamp-2 flex-1 text-sm text-gray-500 dark:text-gray-400">
                    {project.description || t('projects.noDescription')}
                  </p>
                  <p className="mt-3 text-xs text-gray-400">
                    {t('projects.updated', { date: formatDate(project.updatedAt, language) })}
                  </p>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default Projects;
